import { ScopeChangeRequest } from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import { type ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { mustHaveMaxLength } from '@/utils/validx-util'
import { required, validationContext } from 'validx'
import type { AlertErrorHandlerFactory } from '@/features/shell'

/**
 * Who the request is rejected as: the admin declining it, or the admin
 * rejecting it on behalf of the client.
 */
export type ScopeChangeRequestRejectVariant =
  'Decline' | 'RejectOnBehalfOfClient'

export class ScopeChangeRequestRejectDialogViewModel {
  static readonly MAX_REASON_LENGTH = 280

  readonly modal = new ModalViewModel()

  @observable accessor scopeChangeRequest = ScopeChangeRequest.none
  /**
   * The reason the request was rejected
   */
  @observable accessor reason = ''

  /**
   * Validation context for reject reason
   */
  readonly validation = validationContext(this, {
    reason: [
      required('Reason must not be empty.'),
      mustHaveMaxLength(
        ScopeChangeRequestRejectDialogViewModel.MAX_REASON_LENGTH,
        `Reason must be less than ${ScopeChangeRequestRejectDialogViewModel.MAX_REASON_LENGTH} characters`,
      ),
    ],
  })

  constructor(
    readonly variant: ScopeChangeRequestRejectVariant,
    private readonly _scopeChangeRequestStore: ScopeChangeRequestStore,
    private readonly _refreshStatusFilter: () => Promise<void>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Reason input characters remaining text label.
   */
  @computed
  get reasonCharsRemaining() {
    return this.reason
      ? ScopeChangeRequestRejectDialogViewModel.MAX_REASON_LENGTH -
          this.reason.length
      : ScopeChangeRequestRejectDialogViewModel.MAX_REASON_LENGTH
  }

  @action.bound
  reset() {
    this.reason = ''
    this.validation.reset()
  }

  /**
   * Set the reason
   */
  @action.bound
  setReason(reason: string) {
    this.reason = reason
  }

  /**
   * Shows the dialog and sets the scope change request
   * @param scopeChangeRequest
   */
  @action.bound
  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest
    this.validation.reset()
    this.modal.show()
  }

  /**
   * Rejects the scope change request
   */
  reject = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const reject =
      this.variant === 'Decline'
        ? this._scopeChangeRequestStore.rejectScopeChangeRequest(
            this.scopeChangeRequest,
            this.reason,
          )
        : this._scopeChangeRequestStore.rejectScopeChangeRequestOnBehalfOfClient(
            this.scopeChangeRequest,
            this.reason,
          )
    await reject.catch(
      this._createAlertErrorHandler('Rejecting the change request failed'),
    )

    await this._refreshStatusFilter()

    this.reset()
    this.modal.close()
  })
}
